import { Box, Button, TextField, Paper, Typography, Chip, InputAdornment, Divider } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import CorporateFareIcon from '@mui/icons-material/CorporateFare'; // For the small icon next to title
import { updateField } from "../../../../features/editCompany/editCompanySlice";

export default function CompanyDetailsStep() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const draft = useSelector(state => state.editCompany.draft);

  // Common styles for the TextFields to match the "pill" look in the image
  const fieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#fff',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#666',
      marginBottom: '4px',
      position: 'relative',
      transform: 'none',
    },
    '& label': { position: 'relative' },
    '& legend': { display: 'none' },
    '& .MuiInputBase-input': { paddingTop: '10px' }
  };

  return (
    <Box sx={{ minHeight:"100vh", pb: 10, position: "relative" }}>
      {/* Main Card */}
      <Paper
        elevation={0}
        sx={{
          maxWidth: 800,
          mx: "auto",
          mt: 4,
          p: 4,
          borderRadius: '16px',
          border: '1px solid #e0e0e0',
          bgcolor: "#FAFAFA"
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <CorporateFareIcon sx={{ fontSize: 18, color: '#888' }} />
          <Typography variant="subtitle2" fontWeight={700} color="#333">
            Company Details
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" gap={2.5}>
          {/* Name */}
          <Box>
             <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 600 }}>Name</Typography>
             <TextField
                fullWidth
                size="small"
                value={draft.name}
                onChange={(e) => dispatch(updateField({field: "name", value: e.target.value}))}
                sx={fieldStyle}
              />
          </Box>

          {/* Sector & Location Row */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
             <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 600 }}>Sector</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={draft.sector}
                  InputProps={{
                    startAdornment: (
                      <Chip 
                        label="IT" 
                        size="small" 
                        onDelete={() => {}} 
                        deleteIcon={<CloseIcon style={{fontSize: '14px'}}/>}
                        sx={{ borderRadius: '4px', height: 24, mr: 1 }} 
                      />
                    ),
                  }}
                  sx={fieldStyle}
                />
             </Box>
             <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 600 }}>Location</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={draft.location}
                  onChange={(e) => dispatch(updateField( {field: "location", value: e.target.value}))}
                  sx={fieldStyle}
                />
             </Box>
          </Box>

          {/* Description */}
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 600 }}>Description</Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              value={draft.description}
              onChange={(e) => dispatch(updateField({ field: "description", value: e.target.value}))}
              sx={fieldStyle}
            />
          </Box>

          {/* Website */}
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 600 }}>Website</Typography>
            <TextField
              fullWidth
              size="small"
              value={draft.website || "www.google.com"}
              sx={fieldStyle}
            />
          </Box>

          {/* Logotype Section */}
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 600 }}>Logotype</Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_Logo.png" alt="logo" style={{ width: 24, height: 24 }} />
                    <Typography variant="body2" color="textSecondary">image/google-logo.png</Typography>
                </Box>
                <Button 
                    startIcon={<CloseIcon sx={{ bgcolor: '#888', color: '#fff', borderRadius: '50%', p: 0.2, fontSize: '10px !important' }}/>} 
                    sx={{ textTransform: 'none', color: '#666', fontSize: '0.85rem' }}
                >
                    Remove current
                </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* 🔷 Fixed Footer Actions */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: '#fff',
          borderTop: '1px solid #e0e0e0',
          p: 2,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate(`/company/${companyId}/edit/ownership`)}
          sx={{
            bgcolor: '#005682',
            textTransform: 'none',
            borderRadius: '20px',
            px: 4,
            '&:hover': { bgcolor: '#004466' }
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}